import { useMemo } from 'react';
import './PanningVisualizer.css';

interface PanningData {
  left: number;
  right: number;
  center: number;
}

interface PanningVisualizerProps {
  panning: PanningData;
  instruments?: Array<{ type: string; pan?: number }>;
}

/**
 * Stereo field visualizer — Traktor / Ableton inspired.
 * Shows a horizontal L→C→R lane with each instrument as a colored
 * dot positioned by its pan value (-1 = full left, +1 = full right).
 */
export function PanningVisualizer({ panning, instruments }: PanningVisualizerProps) {
  // Stack instruments vertically when several share a pan position.
  // Detect collisions (within ~6% of each other) and assign each a row index
  // so labels never overlap.
  const slots = useMemo(() => {
    if (!instruments) return [] as Array<{ type: string; pan: number; row: number }>;
    const sorted = [...instruments]
      .map((inst) => ({
        type: inst.type,
        pan: Math.max(-1, Math.min(1, inst.pan ?? 0)),
      }))
      .sort((a, b) => a.pan - b.pan);

    const COLLISION = 0.18; // ~9% in screen units
    const rowEndPan: number[] = []; // last pan claimed in each row
    return sorted.map((inst) => {
      let row = 0;
      while (
        rowEndPan[row] !== undefined &&
        Math.abs(inst.pan - rowEndPan[row]) < COLLISION
      ) {
        row++;
      }
      rowEndPan[row] = inst.pan;
      return { ...inst, row };
    });
  }, [instruments]);

  return (
    <div className="panning-visualizer">
      <div className="panning-meta">
        <span className="panning-meta-label">Stereo Field</span>
        <span className="panning-meta-value">L ↔ R</span>
      </div>

      <div className="panning-stage">
        {/* L / C / R rail markers */}
        <div className="panning-rail" aria-hidden="true">
          <span className="panning-tick panning-tick-left">L</span>
          <span className="panning-tick panning-tick-center">C</span>
          <span className="panning-tick panning-tick-right">R</span>
        </div>

        {/* Spectrum bar — minimal: gradient + center tick + dots */}
        <div className="panning-bar">
          <div className="panning-bar-fill" />
          <div className="panning-bar-center" />

          {slots.map((inst, idx) => {
            const left = 50 + inst.pan * 50;
            const tone =
              inst.pan < -0.3 ? 'left' : inst.pan > 0.3 ? 'right' : 'center';
            return (
              <div
                key={`${inst.type}-${idx}`}
                className={`panning-dot panning-dot-${tone}`}
                style={{
                  left: `${left}%`,
                  ['--row' as any]: inst.row,
                }}
                title={`${inst.type} · pan ${inst.pan.toFixed(2)}`}
              >
                <span className="panning-dot-marker" />
                <span className="panning-dot-label">{inst.type}</span>
              </div>
            );
          })}
        </div>

        {/* Mono numeric readout */}
        <div className="panning-readout">
          <div className="panning-readout-cell">
            <span className="panning-readout-label">L</span>
            <span className="panning-readout-value">
              {(panning.left * 100).toFixed(0)}
            </span>
          </div>
          <div className="panning-readout-cell">
            <span className="panning-readout-label">Center</span>
            <span className="panning-readout-value">
              {(panning.center * 100).toFixed(0)}
            </span>
          </div>
          <div className="panning-readout-cell">
            <span className="panning-readout-label">R</span>
            <span className="panning-readout-value">
              {(panning.right * 100).toFixed(0)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
